import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {Popover, PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelXSmall} from "@khanacademy/wonder-blocks-typography";

import {type JsonPath} from "../../types.ts";
import {useCoeditingAPI} from "../../use-coediting-api.ts";

import sparkleIcon from "@phosphor-icons/core/regular/sparkle.svg";

const defaultText = "Try something different";

export default function SparkleButton(props: {label?: string; path: JsonPath}) {
    const {path, label} = props;

    const [inputOpen, setInputOpen] = useState(false);
    const [text, setText] = useState(defaultText);
    const [loading, setLoading] = useState(false);

    const {requestNewValue} = useCoeditingAPI();

    const onSubmit = async () => {
        setLoading(true);

        await requestNewValue(path, text);

        setInputOpen(false);
        setLoading(false);
        setText(defaultText);
    };

    return (
        <Popover
            dismissEnabled
            opened={inputOpen}
            onClose={() => {
                setInputOpen(false);
                setText("");
            }}
            content={
                <PopoverContentCore style={{width: 400}}>
                    <LabelXSmall style={styles.pathLabel}>
                        [{path.join(", ")}]
                    </LabelXSmall>
                    <Strut size={spacing.xxSmall_6} />
                    <LabelMedium>{label || "Make changes to this"}</LabelMedium>
                    <Strut size={spacing.small_12} />
                    <TextField value={text} onChange={setText} />
                    <Strut size={spacing.small_12} />
                    <Button size="small" onClick={onSubmit} spinner={loading}>
                        {t`Send`}
                    </Button>
                </PopoverContentCore>
            }
        >
            {label ? (
                <Button
                    size="small"
                    startIcon={sparkleIcon}
                    kind="tertiary"
                    style={styles.khanmigoButton}
                    onClick={() => setInputOpen(true)}
                >
                    {label}
                </Button>
            ) : (
                <IconButton
                    icon={sparkleIcon}
                    kind="tertiary"
                    actionType="neutral"
                    size="xsmall"
                    style={styles.khanmigoButton}
                    onClick={() => setInputOpen(true)}
                />
            )}
        </Popover>
    );
}

const styles = StyleSheet.create({
    khanmigoButton: {
        justifyContent: "start",
        height: "1em",
        marginInlineStart: 0,
        width: "fit-content",
        color: color.offBlack32,
        ":hover": {
            color: color.blue,
        },
    },
    pathLabel: {
        color: color.offBlack32,
    },
});
